import { useState, useEffect, useRef } from 'react';
import { formatDate, getCurrentWeekDates } from '../../../../lib/utils';
import { ReactComponent as EditSvg } from '../../../../assets/svg/edit.svg';
import { ReactComponent as LoadingSvg } from '../../../../assets/svg/loading-half-status.svg';
import { WEIGHT_UNIT } from '../../../../types/enum';
import { convertWeight } from '../../../../lib/utils';
import request from '../../../../lib/request';
import BottomDialog from '../../../../components/bottomDialog';
import TextInput from '../../../../components/textInput';

const calculateDailyTotalWorkoutHours = (list) => {
  const seconds = list.reduce((acc, cur) => acc + (cur.duration ?? 0), 0);
  return Math.floor(seconds / 60);
};

const getInputLabel = (key) => {
  if (key === 'weight') {
    return 'Goal Weight';
  }
  if (key === 'dailyCalorie') {
    return 'Daily Calorie';
  }
  if (key === 'dailyWorkoutHours') {
    return 'How much time will you workout out each day? (mins)';
  }
  if (key === 'weeklyWorkoutDays') {
    return 'How many times will you workout out each week? (days)';
  }
  if (key === 'dailyProtein') {
    return 'Daily Protein (g)';
  }
  if (key === 'dailyCarbs') {
    return 'Daily Carbs (g)';
  }
  if (key === 'dailyFat') {
    return 'Daily Fat (g)';
  }
};

const calculateDailyMacronutrients = (ingredients) => {
  const protein = ingredients.reduce(
    (acc, cur) => acc + (cur.proteins ?? 0),
    0
  );
  const carbs = ingredients.reduce(
    (acc, cur) => acc + (cur.carbohydrates ?? 0),
    0
  );
  const fat = ingredients.reduce((acc, cur) => acc + (cur.fats ?? 0), 0);
  return { protein, carbs, fat };
};

const CurrentGoal = ({ goal, user, getUserGoal, preference }) => {
  const bottomDialogRef = useRef(null);

  const [dailyTotalCalorie, setDailyTotalCalorie] = useState(0);
  const [dailyProtein, setDailyProtein] = useState(0);
  const [dailyCarbs, setDailyCarbs] = useState(0);
  const [dailyFat, setDailyFat] = useState(0);
  const [dailyWorkoutHours, setDailyTotalWorkoutHours] = useState(0);
  const [weeklyTotalWorkoutDays, setWeeklyTotalWorkoutDays] = useState(0);
  const [loading, setLoading] = useState(false);
  const [updateKey, setUpdateKey] = useState('');
  const [newGoal, setNewGoal] = useState({});
  const [isProgramActive, setIsProgramActive] = useState(false);

  useEffect(() => {
    const today = formatDate(new Date());
    getTodayTotalCalories(today);
    getWorkoutLog(today);

    const currentWeeks = getCurrentWeekDates();
    getWorkoutTracker(currentWeeks[0], currentWeeks[currentWeeks.length - 1]);

    for (const program of user.programs) {
      if (new Date(program.endDate).getTime() > new Date().getTime()) {
        setIsProgramActive(true);
        break;
      }
    }
  }, []);

  const getTodayTotalCalories = async (date) => {
    try {
      const { result } = await request.get(`/meal/today?date=${date}`);
      if (result) {
        setDailyTotalCalorie(result.totalCalories);

        let protein = 0;
        let carbs = 0;
        let fat = 0;

        for (const item of result.list) {
          const {
            protein: itemProtein,
            carbs: itemCarbs,
            fat: itemFat,
          } = calculateDailyMacronutrients(item.ingredients);
          protein += itemProtein;
          carbs += itemCarbs;
          fat += itemFat;
        }

        setDailyProtein(protein.toFixed(1));
        setDailyCarbs(carbs.toFixed(1));
        setDailyFat(fat.toFixed(1));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getWorkoutLog = async (date) => {
    try {
      const startDate = new Date(`${date}T00:00:00`).toISOString();
      const endDate = new Date(`${date}T23:59:59.999`).toISOString();

      const { result } = await request.get(
        `/workout/log?date=${date}&startDate=${startDate}&endDate=${endDate}`
      );
      if (result) {
        const mins = calculateDailyTotalWorkoutHours(result);
        setDailyTotalWorkoutHours(mins);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getWorkoutTracker = async (startDate, endDate) => {
    try {
      const { result } = await request.get(
        `/workout?startDate=${startDate}&endDate=${endDate}`
      );
      if (result) {
        setWeeklyTotalWorkoutDays(result.length);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openBottomDialog = (key) => () => {
    if (bottomDialogRef.current) {
      setUpdateKey(key);
      bottomDialogRef.current.showModal();
    }
  };

  const closeBottomDialog = () => {
    if (bottomDialogRef.current) {
      bottomDialogRef.current.close();
      setNewGoal({});
    }
  };

  const handleChangeInput = (e) => {
    setNewGoal((prev) => ({
      ...prev,
      [updateKey]: Number(e.target.value),
    }));
  };

  const handleUpdateGoal = async () => {
    try {
      if (goal.id) {
        if (preference.weightUnit === WEIGHT_UNIT.LB) {
          newGoal.weight = convertWeight(
            newGoal.weight,
            WEIGHT_UNIT.LB,
            WEIGHT_UNIT.KG
          );
        }
        const { result } = await request.put(`/user/goal/${goal.id}`, newGoal);
        if (result) {
          await getUserGoal();
          closeBottomDialog();
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    goal &&
    Object.keys(goal).length > 0 && (
      <>
        <div className='space-y-6 text-white p-4 bg-secondary rounded-2xl'>
          {goal.weeks && (
            <div className='text-center py-3 text-sm bg-neutral-800 rounded-xl'>
              <span className='text-gray-400'>About </span>
              <span className='font-bold text-white'>{goal.weeks} weeks</span>
              <span className='text-gray-400'> to achieve your goal</span>
            </div>
          )}

          <div className='space-y-3'>
            <h1 className='font-bold text-lg flex items-center gap-2'>
              Weight Goal
            </h1>
            <div className='bg-neutral-800 rounded-2xl p-4 flex justify-between items-center hover:bg-neutral-700 transition-colors duration-200'>
              <div className='text-gray-300'>Target Weight</div>
              <div
                className='text-right'
                onClick={openBottomDialog('weight')}
              >
                <div className='flex gap-3 items-center justify-end cursor-pointer'>
                  <div className='font-bold text-lg'>
                    {`${goal.weight.toFixed(1)} ${
                      preference.weightUnit === WEIGHT_UNIT.LB ? 'lb' : 'kg'
                    }`}
                  </div>
                  <EditSvg className='size-4' />
                </div>
                <div className='text-sm text-gray-500 mt-1'>
                  Current:{' '}
                  {user.weightLogs[0]?.weight
                    ? preference.weightUnit === WEIGHT_UNIT.LB
                      ? `${convertWeight(
                          user.weightLogs[0]?.weight,
                          WEIGHT_UNIT.KG,
                          WEIGHT_UNIT.LB
                        ).toFixed(1)} lb`
                      : `${user.weightLogs[0]?.weight.toFixed(1)} kg`
                    : preference.weightUnit === WEIGHT_UNIT.LB
                    ? `${convertWeight(
                        user.weight,
                        WEIGHT_UNIT.KG,
                        WEIGHT_UNIT.LB
                      ).toFixed(1)} lb`
                    : `${user.weight.toFixed(1)} kg`}
                </div>
              </div>
            </div>
          </div>

          <div className='space-y-3'>
            <h1 className='font-bold text-lg flex items-center gap-2'>
              Daily Calories
            </h1>
            <div className='bg-neutral-800 rounded-2xl p-4 space-y-4'>
              <div className='flex justify-between items-center'>
                <div className='text-gray-300'>Target Intake</div>
                <div
                  className='text-right'
                  onClick={
                    isProgramActive ? null : openBottomDialog('dailyCalorie')
                  }
                >
                  <div className='flex gap-3 items-center justify-end cursor-pointer'>
                    <div className='font-bold text-lg'>
                      {goal.dailyCalorie} Cal
                    </div>
                    {isProgramActive ? (
                      <div className='text-gray-400 text-xs px-2 py-1 bg-neutral-700 rounded-lg'>
                        planned
                      </div>
                    ) : (
                      <EditSvg className='size-4' />
                    )}
                  </div>
                  <div className='text-sm text-gray-500 mt-1'>
                    Progress: {dailyTotalCalorie} / {goal.dailyCalorie} Cal
                  </div>
                </div>
              </div>
              {(goal.dailyProtein || goal.dailyCarbs || goal.dailyFat) && (
                <>
                  <div className='h-px bg-neutral-700'></div>
                  <div>
                    <div className='flex justify-between items-center'>
                      <div className='text-gray-300'>Daily Protein</div>
                      <div
                        className='text-right'
                        onClick={
                          isProgramActive
                            ? null
                            : openBottomDialog('dailyProtein')
                        }
                      >
                        <div className='flex gap-3 items-center justify-end cursor-pointer'>
                          <div className='font-bold text-lg'>
                            {goal.dailyProtein} g
                          </div>
                          {isProgramActive ? (
                            <div className='text-gray-400 text-xs px-2 py-1 bg-neutral-700 rounded-lg'>
                              planned
                            </div>
                          ) : (
                            <EditSvg className='size-4' />
                          )}
                        </div>
                        <div className='text-sm text-gray-500 mt-1'>
                          Today: {dailyProtein} / {goal.dailyProtein} g
                        </div>
                      </div>
                    </div>
                    <div className='flex justify-between items-center'>
                      <div className='text-gray-300'>Daily Carbs</div>
                      <div
                        className='text-right'
                        onClick={
                          isProgramActive
                            ? null
                            : openBottomDialog('dailyCarbs')
                        }
                      >
                        <div className='flex gap-3 items-center justify-end cursor-pointer'>
                          <div className='font-bold text-lg'>
                            {goal.dailyCarbs} g
                          </div>
                          {isProgramActive ? (
                            <div className='text-gray-400 text-xs px-2 py-1 bg-neutral-700 rounded-lg'>
                              planned
                            </div>
                          ) : (
                            <EditSvg className='size-4' />
                          )}
                        </div>
                        <div className='text-sm text-gray-500 mt-1'>
                          Today: {dailyCarbs} / {goal.dailyCarbs} g
                        </div>
                      </div>
                    </div>
                    <div className='flex justify-between items-center'>
                      <div className='text-gray-300'>Daily Fat</div>
                      <div
                        className='text-right'
                        onClick={
                          isProgramActive ? null : openBottomDialog('dailyFat')
                        }
                      >
                        <div className='flex gap-3 items-center justify-end cursor-pointer'>
                          <div className='font-bold text-lg'>
                            {goal.dailyFat} g
                          </div>
                          {isProgramActive ? (
                            <div className='text-gray-400 text-xs px-2 py-1 bg-neutral-700 rounded-lg'>
                              planned
                            </div>
                          ) : (
                            <EditSvg className='size-4' />
                          )}
                        </div>
                        <div className='text-sm text-gray-500 mt-1'>
                          Today: {dailyFat} / {goal.dailyFat} g
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className='space-y-3'>
            <h1 className='font-bold text-lg flex items-center gap-2'>
              Workout Goals
            </h1>
            <div className='bg-neutral-800 rounded-2xl p-4 space-y-4'>
              <div className='flex justify-between items-center'>
                <div className='text-gray-300'>Daily Duration</div>
                <div
                  className='text-right'
                  onClick={openBottomDialog('dailyWorkoutHours')}
                >
                  <div className='flex gap-3 items-center justify-end cursor-pointer'>
                    <div className='font-bold text-lg'>
                      {goal.dailyWorkoutHours} mins
                    </div>
                    <EditSvg className='size-4' />
                  </div>
                  <div className='text-sm text-gray-500 mt-1'>
                    Today: {dailyWorkoutHours} / {goal.dailyWorkoutHours} mins
                  </div>
                </div>
              </div>
              <div className='h-px bg-neutral-700'></div>
              <div className='flex justify-between items-center'>
                <div className='text-gray-300'>Weekly Frequency</div>
                <div
                  className='text-right'
                  onClick={openBottomDialog('weeklyWorkoutDays')}
                >
                  <div className='flex gap-3 items-center justify-end cursor-pointer'>
                    <div className='font-bold text-lg'>
                      {goal.weeklyWorkoutDays} days
                    </div>
                    <EditSvg className='size-4' />
                  </div>
                  <div className='text-sm text-gray-500 mt-1'>
                    This week: {weeklyTotalWorkoutDays} /{' '}
                    {goal.weeklyWorkoutDays} days
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <BottomDialog
          dialogRef={bottomDialogRef}
          closeDialog={closeBottomDialog}
        >
          <div className='text-sm space-y-4 px-3'>
            <TextInput
              label={getInputLabel(updateKey)}
              onChange={handleChangeInput}
              value={newGoal[updateKey]}
            />
            <div className='flex items-center justify-between'>
              <button
                className='border rounded-xl px-2 py-1'
                onClick={closeBottomDialog}
              >
                Cancel
              </button>
              <button
                className='border rounded-xl px-2 py-1'
                onClick={handleUpdateGoal}
              >
                Save
              </button>
            </div>
          </div>
          {loading && (
            <div className='absolute animate-bounce inset-x-0 top-4 flex justify-center items-center'>
              <LoadingSvg className='animate-spin' />
            </div>
          )}
        </BottomDialog>
      </>
    )
  );
};

export default CurrentGoal;
